<template>
  <div class="life-page">
    <section class="life-panel">
      <div class="life-panel__head">
        <h2 class="life-panel__title">履约订单查询</h2>
        <span class="life-muted">运营跨小区统一管控</span>
      </div>
      <div class="life-filter">
        <el-select
          v-model="filters.communityId"
          placeholder="全部小区"
          clearable
          filterable
          style="width: 200px"
        >
          <el-option
            v-for="item in communityOptions"
            :key="item.communityId"
            :label="item.name"
            :value="item.communityId"
          />
        </el-select>
        <el-input v-model="filters.personName" placeholder="联系人" clearable style="width: 140px" @keyup.enter.native="search" />
        <el-input v-model="filters.personTel" placeholder="联系电话" clearable style="width: 140px" @keyup.enter.native="search" />
        <el-input v-model="filters.orderId" placeholder="本地订单号" clearable style="width: 180px" @keyup.enter.native="search" />
        <el-input v-model="filters.externalOrderId" placeholder="啄木鸟订单号" clearable style="width: 160px" @keyup.enter.native="search" />
        <el-select v-model="filters.state" placeholder="订单状态" clearable style="width: 150px">
          <el-option v-for="item in stateOptions" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="search">查询</el-button>
        <el-button icon="el-icon-refresh" @click="reset">重置</el-button>
      </div>
    </section>

    <section class="life-panel">
      <div class="life-panel__head">
        <h2 class="life-panel__title">履约订单</h2>
        <span class="life-muted">只读溯源；不可手工改对方履约状态</span>
      </div>
      <el-table v-loading="loading" :data="rows" border>
        <el-table-column prop="communityName" label="小区" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.communityName || scope.row.communityId || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="productName" label="服务产品" min-width="140" show-overflow-tooltip />
        <el-table-column prop="personName" label="联系人" width="90" />
        <el-table-column prop="personTel" label="电话" width="120" />
        <el-table-column prop="appointmentTime" label="预约时间" width="160" />
        <el-table-column label="预付/实收" width="120" align="center">
          <template slot-scope="scope">
            <span class="life-money">{{ scope.row.prepayAmount }}</span>
            <span class="life-muted"> / </span>
            <span class="life-money">{{ scope.row.receivedAmount || '0.00' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="externalOrderId" label="外部单号" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="life-id">{{ scope.row.externalOrderId || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="120" align="center">
          <template slot-scope="scope">
            <el-tag :type="stateTag(scope.row.state)" size="small">
              {{ scope.row.stateName || scope.row.state }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="masterName" label="师傅" width="90" show-overflow-tooltip />
        <el-table-column prop="createTime" label="创建时间" width="160" />
        <el-table-column label="操作" width="180" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" icon="el-icon-view" @click="openDetail(scope.row)">溯源</el-button>
            <el-button
              v-if="canRetry(scope.row.state)"
              type="text"
              icon="el-icon-refresh"
              @click="handleRetry(scope.row)"
            >补推</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="life-pagination"
        :current-page.sync="page.current"
        :page-size="page.size"
        :page-sizes="[10, 20, 50]"
        :total="page.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="changeSize"
        @current-change="load"
      />
    </section>

    <el-drawer
      title="履约订单溯源"
      :visible.sync="detailVisible"
      size="720px"
      custom-class="hs-order-drawer"
      @closed="onDetailClosed"
    >
      <div v-loading="detailLoading" class="hs-detail">
        <template v-if="detail">
          <section class="hs-block">
            <h3>订单概要</h3>
            <el-descriptions :column="2" border size="small">
              <el-descriptions-item label="本地订单">{{ detail.orderId }}</el-descriptions-item>
              <el-descriptions-item label="状态">{{ detail.stateName || detail.state }}</el-descriptions-item>
              <el-descriptions-item label="小区">{{ detail.communityName || detail.communityId || '-' }}</el-descriptions-item>
              <el-descriptions-item label="产品">{{ detail.productName || detail.productId }}</el-descriptions-item>
              <el-descriptions-item label="预约时间">{{ detail.appointmentTime }}</el-descriptions-item>
              <el-descriptions-item label="联系人">{{ detail.personName }} {{ detail.personTel }}</el-descriptions-item>
              <el-descriptions-item label="师傅">{{ detail.masterName || '-' }} {{ detail.masterPhone || '' }}</el-descriptions-item>
              <el-descriptions-item label="服务地址" :span="2">{{ detail.address }}</el-descriptions-item>
              <el-descriptions-item label="预付/实收">
                <span class="life-money">{{ detail.prepayAmount }}</span> /
                <span class="life-money">{{ detail.receivedAmount || '0.00' }}</span>
              </el-descriptions-item>
              <el-descriptions-item label="退款">
                {{ detail.refundState || '-' }}
                <span v-if="detail.refundAmount"> / ¥{{ detail.refundAmount }}</span>
              </el-descriptions-item>
            </el-descriptions>
            <div v-if="detail.canRetrySync" class="hs-actions">
              <el-button type="primary" size="small" icon="el-icon-refresh" :loading="retrying" @click="handleRetry(detail)">
                补推同步
              </el-button>
              <span class="life-muted">仅同步 createOrder；不可改对方上门状态</span>
            </div>
          </section>

          <section class="hs-block">
            <h3>履约溯源链路</h3>
            <p class="life-muted hs-hint">本平台下单收款 → 三方上门履约，不是导购点击归因。</p>
            <el-steps direction="vertical" :active="(detail.attribution || []).length" finish-status="success">
              <el-step
                v-for="item in (detail.attribution || [])"
                :key="item.code"
                :title="item.title"
                :description="formatAttr(item)"
              />
            </el-steps>
          </section>

          <section class="hs-block">
            <h3>支付请求</h3>
            <el-table :data="detail.payRequests || []" border size="mini" empty-text="暂无支付记录">
              <el-table-column prop="amountType" label="类型" width="90" />
              <el-table-column prop="amountYuan" label="金额" width="90" />
              <el-table-column prop="state" label="状态" width="90" />
              <el-table-column prop="extOrderId" label="支付单号" min-width="140" />
              <el-table-column prop="createTime" label="时间" width="160" />
            </el-table>
          </section>

          <section class="hs-block">
            <h3>进度时间线</h3>
            <el-timeline>
              <el-timeline-item
                v-for="(item, index) in (detail.timeline || [])"
                :key="`${item.title}-${index}`"
                :timestamp="item.time || ''"
                placement="top"
              >
                <p class="hs-timeline-title">{{ item.title }}</p>
                <p class="life-muted">{{ item.description }}</p>
              </el-timeline-item>
            </el-timeline>
            <div v-if="!(detail.timeline && detail.timeline.length)" class="life-muted">暂无进度事件</div>
          </section>
        </template>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import { getOrder, listOrders, retrySyncOrder } from '@/api/life/homeServiceApi'
import { listAdminCommunitys } from '@/api/owner/adminOwnerApi'

const STATE_OPTIONS = [
  { value: 'UNPAID', label: '待支付' },
  { value: 'PAID_LOCAL', label: '本地已付' },
  { value: 'SYNCING', label: '同步中' },
  { value: 'WAIT_ACCEPT', label: '等待安排' },
  { value: 'DISPATCHED', label: '已派单' },
  { value: 'ACCEPTED', label: '已接单' },
  { value: 'VISITING', label: '上门中' },
  { value: 'COMPLETED', label: '已完成' },
  { value: 'CANCELLED', label: '已取消' },
  { value: 'REFUNDING', label: '退款中' },
  { value: 'REFUNDED', label: '已退款' },
  { value: 'SYNC_FAILED', label: '同步失败' }
]

export default {
  name: 'HomeServiceOrderList',
  data() {
    return {
      loading: false,
      detailLoading: false,
      retrying: false,
      detailVisible: false,
      rows: [],
      detail: null,
      communityOptions: [],
      stateOptions: STATE_OPTIONS,
      filters: {
        communityId: '',
        personName: '',
        personTel: '',
        orderId: '',
        externalOrderId: '',
        state: ''
      },
      page: { current: 1, size: 10, total: 0 }
    }
  },
  created() {
    this.loadCommunities()
    this.load()
  },
  methods: {
    async loadCommunities() {
      try {
        const res = await listAdminCommunitys({ page: 1, row: 500 })
        this.communityOptions = res.data || []
      } catch (_) {
        this.communityOptions = []
      }
    },
    async load() {
      this.loading = true
      try {
        const res = await listOrders({
          ...this.filters,
          page: this.page.current,
          row: this.page.size
        })
        this.rows = res.data || []
        this.page.total = Number(res.total || 0)
      } catch (error) {
        this.$message.error(error.message || '加载订单失败')
      } finally {
        this.loading = false
      }
    },
    search() {
      this.page.current = 1
      this.load()
    },
    reset() {
      this.filters = {
        communityId: '',
        personName: '',
        personTel: '',
        orderId: '',
        externalOrderId: '',
        state: ''
      }
      this.search()
    },
    changeSize(size) {
      this.page.size = size
      this.load()
    },
    canRetry(state) {
      return ['PAID_LOCAL', 'SYNCING', 'SYNC_FAILED'].includes(state)
    },
    stateTag(state) {
      return {
        UNPAID: 'info',
        PAID_LOCAL: 'warning',
        SYNCING: 'warning',
        WAIT_ACCEPT: '',
        DISPATCHED: '',
        ACCEPTED: '',
        VISITING: 'warning',
        COMPLETED: 'success',
        CANCELLED: 'info',
        REFUNDING: 'warning',
        REFUNDED: 'success',
        SYNC_FAILED: 'danger'
      }[state] || 'info'
    },
    formatAttr(item) {
      const value = item.value || ''
      const detail = item.detail || ''
      return detail ? `${value}｜${detail}` : value
    },
    async openDetail(row) {
      this.detailVisible = true
      this.detailLoading = true
      this.detail = null
      try {
        const res = await getOrder({ orderId: row.orderId })
        this.detail = res.data || res
      } catch (error) {
        this.$message.error(error.message || '加载详情失败')
        this.detailVisible = false
      } finally {
        this.detailLoading = false
      }
    },
    onDetailClosed() {
      this.detail = null
    },
    handleRetry(row) {
      this.$confirm(`确认补推同步订单「${row.orderId}」到啄木鸟？`, '补推确认', { type: 'warning' })
        .then(async () => {
          this.retrying = true
          try {
            const res = await retrySyncOrder({ orderId: row.orderId })
            const data = res.data || res
            this.$message.success(`补推完成：${data.stateName || data.state || '已提交'}`)
            await this.load()
            if (this.detailVisible && this.detail && this.detail.orderId === row.orderId) {
              await this.openDetail(row)
            }
          } catch (error) {
            this.$message.error(error.message || '补推失败')
          } finally {
            this.retrying = false
          }
        })
        .catch(() => {})
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../lifeService/life.scss';

.hs-detail {
  padding: 0 20px 24px;
}

.hs-block {
  margin-bottom: 24px;

  h3 {
    margin: 0 0 12px;
    font-size: 15px;
    color: #172033;
  }
}

.hs-hint {
  margin: 0 0 12px;
  font-size: 12px;
}

.hs-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.hs-timeline-title {
  margin: 0 0 4px;
  font-weight: 600;
  color: #172033;
}
</style>
